import React, { useState, useEffect, useCallback } from "react"
import { Spin, message } from "antd"
import { useSelector } from "react-redux"
import { translations } from "../../utils/Translation/staticTranslation"
import {
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../../utils/modulePermissionSidebar"
import CampaignStats from "../../components/Campaigns/CampaignStats"
import CampaignsTableWithFilters from "../../components/Campaigns/CampaignsTableWithFilters"
import CreateCampaignModal from "../../components/Campaigns/CreateCampaignModal"
import ViewCampaignModal from "../../components/Campaigns/ViewCampaignModal"
import CampaignStatisticsModal from "../../components/Campaigns/CampaignStatisticsModal"
import { Campaigns as CampaignsAPI } from "../../Apis/campaignsApi"
import "../../components/Campaigns/Campaigns.css"

const TYPE_MAPPING = {
  Promotional: "promotional",
  Updates: "updates",
  Seasonal: "seasonal",
  Welcome: "welcome",
}

const STATUS_MAPPING = {
  Scheduled: "scheduled",
  Draft: "draft",
  Saved: "saved",
  "Send Immediately": "send_immediately",
  Queue: "queue",
  Completed: "completed",
  Canceled: "canceled",
  Failed: "failed",
}

const Campaigns = () => {
  const user = useSelector((state) => state.user.user)
  const campaignsReadOnly = isProfileModuleReadOnly(user, "campaigns")
  const campaignsCanDelete = !isProfileModuleDeleteBlocked(user, "campaigns")
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.campaignsPage
  
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [isStatisticsModalVisible, setIsStatisticsModalVisible] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [editCampaignData, setEditCampaignData] = useState(null)
  const [isViewMode, setIsViewMode] = useState(false)
  const [campaignsData, setCampaignsData] = useState([])
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState({
    total: 0,
    scheduled: 0,
    completed: 0,
  })
  const [filters, setFilters] = useState({
    search: "",
    type: "All Types",
    status: "All Status",
    page: 1,
    pageSize: 10,
  })
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  const fetchCampaigns = useCallback(async () => {
    setLoading(true)
    try {
      const apiType = filters.type && filters.type !== "All Types" 
        ? (TYPE_MAPPING[filters.type] || filters.type.toLowerCase())
        : ""
      const apiStatus = filters.status && filters.status !== "All Status"
        ? (STATUS_MAPPING[filters.status] || filters.status.toLowerCase())
        : ""

      const response = await CampaignsAPI.getCampaignsList({
        search: filters.search,
        type: apiType,
        status: apiStatus,
        page: filters.page,
        pageSize: filters.pageSize,
      })

      if (response.status === 200) {
        const data = response.data
        const lang = localStorage.getItem("language") || "ar"
        
        const rawCampaigns = Array.isArray(data) 
          ? data 
          : data.results || data.data || data.campaigns || []
        
        const total = data.count || data.total || rawCampaigns.length || 0

        let transformedCampaigns = rawCampaigns.map((campaign) => ({
          id: campaign.id,
          campaign: campaign.title || "",
          creator: lang === "ar" 
            ? (campaign.created_by_name_ar || campaign.created_by_name_en || "")
            : (campaign.created_by_name_en || campaign.created_by_name_ar || ""),
          description: campaign.description || "",
          type: campaign.type || "",
          status: campaign.status || "",
          audience: lang === "ar" 
            ? (campaign.audience_preset_arabic || campaign.audience_preset || "")
            : (campaign.audience_preset || ""),
          scheduledOnDate: campaign.scheduled_on_date || "",
          scheduledOnTime: campaign.scheduled_on_time || "",
          scheduledOnTitle: campaign.scheduled_on_title || "",
          sendImmediately: campaign.send_immediately || false,
          createdOn: campaign.time_created || "",
          originalData: campaign,
        }))

        // Client-side filtering by campaign name if search is provided
        if (filters.search && filters.search.trim()) {
          const searchTerm = filters.search.trim().toLowerCase()
          transformedCampaigns = transformedCampaigns.filter((campaign) => {
            const campaignName = (campaign.campaign || "").toLowerCase()
            return campaignName.includes(searchTerm)
          })
          // Update total to reflect filtered results
          const filteredTotal = transformedCampaigns.length
          setPagination((prev) => ({
            ...prev,
            total: filteredTotal,
            current: 1,
          }))
        } else {
          setPagination((prev) => ({
            ...prev,
            total: total,
            current: filters.page,
          }))
        }

        setCampaignsData(transformedCampaigns)

        if (data.stats) {
          setStats(data.stats)
        } else {
          const scheduledCount = rawCampaigns.filter(
            (c) => c.status === "Scheduled" || c.status === "scheduled"
          ).length
          const completedCount = rawCampaigns.filter(
            (c) => c.status === "Completed" || c.status === "completed"
          ).length
          setStats({
            total: total,
            scheduled: scheduledCount,
            completed: completedCount,
          })
        }
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error)
      message.error(t?.failed_to_fetch_campaigns || "Failed to fetch campaigns. Please try again.")
      setCampaignsData([])
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCampaigns()
    }, filters.search ? 500 : 0)

    return () => clearTimeout(timer)
  }, [filters, fetchCampaigns])

  const handleSearchChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
      page: 1,
    }))
  }

  const handleStatusFilterChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      status: value,
      page: 1,
    }))
  }

  const handleTypeFilterChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      type: value,
      page: 1,
    }))
  }

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      page: page,
    }))
  }

  const handleView = (record) => {
    setSelectedCampaign(record)
    setIsViewModalVisible(true)
  }

  const handleEdit = async (record) => {
    if (campaignsReadOnly) return
    // If called from view modal, close it first
    setIsViewModalVisible(false)
    
    // Fetch full campaign details to get all fields including subject_en and subject_ar
    try {
      setLoading(true)
      const campaignId = record.originalData?.id || record.id
      if (campaignId) {
        const response = await CampaignsAPI.getCampaignById(campaignId)
        if (response.status === 200) {
          const fullCampaignData = response.data
          // Merge the full data with the record
          setEditCampaignData({
            ...record,
            originalData: fullCampaignData,
          })
        } else {
          // Fallback to record if fetch fails
          setEditCampaignData(record)
        }
      } else {
        setEditCampaignData(record)
      }
    } catch (error) {
      console.error("Error fetching campaign details:", error)
      // Fallback to record if fetch fails
      setEditCampaignData(record)
    } finally {
      setLoading(false)
      setIsViewMode(false)
      setIsModalVisible(true)
    }
  }

  const handleEditFromView = async () => {
    if (campaignsReadOnly) return
    if (selectedCampaign) {
      setIsViewModalVisible(false)
      
      // Fetch full campaign details to get all fields including subject_en and subject_ar
      try {
        setLoading(true)
        const campaignId = selectedCampaign.originalData?.id || selectedCampaign.id
        if (campaignId) {
          const response = await CampaignsAPI.getCampaignById(campaignId)
          if (response.status === 200) {
            const fullCampaignData = response.data
            // Merge the full data with the selected campaign
            setEditCampaignData({
              ...selectedCampaign,
              originalData: fullCampaignData,
            })
          } else {
            setEditCampaignData(selectedCampaign)
          }
        } else {
          setEditCampaignData(selectedCampaign)
        }
      } catch (error) {
        console.error("Error fetching campaign details:", error)
        setEditCampaignData(selectedCampaign)
      } finally {
        setLoading(false)
        setIsViewMode(false)
        setIsModalVisible(true)
      }
    }
  }

  const handleDelete = async (record) => {
    if (!campaignsCanDelete) return
    if (!record?.id) {
      message.error(t?.campaign_id_missing || "Campaign ID is missing")
      return
    }

    try {
      setLoading(true)
      const response = await CampaignsAPI.deleteCampaign(record.id)
      
      if (response.status === 200 || response.status === 204) {
        message.success(t?.campaign_deleted_successfully || "Campaign deleted successfully")
        // Refresh the campaigns list
        await fetchCampaigns()
      }
    } catch (error) {
      console.error("Error deleting campaign:", error)
      message.error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        t?.failed_to_delete_campaign || "Failed to delete campaign. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCampaign = () => {
    if (campaignsReadOnly) return
    setEditCampaignData(null)
    setIsViewMode(false)
    setIsModalVisible(true)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
    setEditCampaignData(null)
    setIsViewMode(false)
  }

  const handleViewModalClose = () => {
    setIsViewModalVisible(false)
    setSelectedCampaign(null)
  }

  const handleViewStatistics = () => {
    setIsStatisticsModalVisible(true)
  }

  const handleStatisticsModalClose = () => {
    setIsStatisticsModalVisible(false)
  }

  const handleViewDetails = async () => {
    if (selectedCampaign) {
      setIsViewModalVisible(false)
      
      // Fetch full campaign details to get all fields including subject_en and subject_ar
      try {
        setLoading(true)
        const campaignId = selectedCampaign.originalData?.id || selectedCampaign.id
        if (campaignId) {
          const response = await CampaignsAPI.getCampaignById(campaignId)
          if (response.status === 200) {
            const fullCampaignData = response.data
            // Merge the full data with the selected campaign
            setEditCampaignData({
              ...selectedCampaign,
              originalData: fullCampaignData,
            })
          } else {
            setEditCampaignData(selectedCampaign)
          }
        } else {
          setEditCampaignData(selectedCampaign)
        }
      } catch (error) {
        console.error("Error fetching campaign details:", error)
        setEditCampaignData(selectedCampaign)
      } finally {
        setLoading(false)
        setIsViewMode(true)
        setIsModalVisible(true)
      }
    }
  }

  const handleModalSubmit = async (formData) => {
    if (campaignsReadOnly) return
    try {
      setLoading(true)
      
      // Check if we're editing an existing campaign
      if (editCampaignData) {
        const campaignId = editCampaignData.originalData?.id || editCampaignData.id
        if (campaignId) {
          const response = await CampaignsAPI.updateCampaign(campaignId, formData)
          
          if (response.status === 200 || response.status === 201) {
            message.success(t?.campaign_updated_successfully || "Campaign updated successfully")
            // Refresh the campaigns list
            await fetchCampaigns()
            setIsModalVisible(false)
            setEditCampaignData(null)
          }
        } else {
          message.error(t?.campaign_id_not_found || "Campaign ID not found. Cannot update campaign.")
        }
      } else {
        // Create new campaign
        const response = await CampaignsAPI.createCampaign(formData)
        
        if (response.status === 200 || response.status === 201) {
          message.success(t?.campaign_created_successfully || "Campaign created successfully")
          // Refresh the campaigns list
          await fetchCampaigns()
          setIsModalVisible(false)
        }
      }
    } catch (error) {
      console.error("Error saving campaign:", error)
      const action = editCampaignData ? "update" : "create"
      message.error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        (action === "update" 
          ? (t?.failed_to_update_campaign || "Failed to update campaign. Please try again.")
          : (t?.failed_to_create_campaign || "Failed to create campaign. Please try again."))
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="campaigns-page-container">
      {/* Page Title */}
      <h1 className="campaigns-page-title">
        {t?.campaigns || "Campaigns"}
      </h1>

      {/* Summary Cards */}
      <CampaignStats stats={stats} />

      {/* Table with Filters */}
      {loading ? (
        <div className="campaigns-loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <CampaignsTableWithFilters
          data={campaignsData}
          loading={loading}
          searchValue={filters.search}
          statusFilter={filters.status}
          typeFilter={filters.type}
          pagination={pagination}
          onSearchChange={handleSearchChange}
          onStatusFilterChange={handleStatusFilterChange}
          onTypeFilterChange={handleTypeFilterChange}
          onPageChange={handlePageChange}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreateCampaign={handleCreateCampaign}
          readOnly={campaignsReadOnly}
          canDelete={campaignsCanDelete}
        />
      )}

      {/* View Campaign Modal */}
      <ViewCampaignModal
        visible={isViewModalVisible}
        onClose={handleViewModalClose}
        campaign={selectedCampaign}
        onEdit={handleEditFromView}
        onViewDetails={handleViewDetails}
        onViewStatistics={handleViewStatistics}
        readOnly={campaignsReadOnly}
      />

      {/* Campaign Statistics Modal */}
      <CampaignStatisticsModal
        visible={isStatisticsModalVisible}
        onCancel={handleStatisticsModalClose}
        campaignId={
          selectedCampaign?.originalData?.id || selectedCampaign?.id || null
        }
      
        readOnly={campaignsReadOnly}
        canDelete={campaignsCanDelete}
      />

      {/* Create/Edit Campaign Modal */}
      <CreateCampaignModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        initialData={editCampaignData}
        viewMode={isViewMode}
      />
    </div>
  )
}

export default Campaigns
